import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Chip,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar
} from '@mui/material';
import { EditOutlinedIcon, OpenInNewIcon, LinkOffIcon, DevicesIcon } from 'icons/materialIcons';
import NotificationSnackbar from '../common/notification/NotificationSnackbar';
import avatarPlaceholder from '../../images/avatar-cropped.png';

const AccessoryTable = ({
  accessories,
  userCards,
  onEditAccessory,
  onDeleteAccessory,
  onUnmapAccessory,
  selectedCardId,
  setSelectedCardId,
  isCardLevel
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAccessory, setSelectedAccessory] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'activated': return 'success';
      case 'available': return 'info';
      case 'disabled': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    }).format(date);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleActionClick = (accessory, action) => {
    setSelectedAccessory(accessory);
    setActionType(action);
    setDialogOpen(true);
  };

  const handleActionConfirm = async () => {
    try {
      if (actionType === 'unmap') {
        await onUnmapAccessory(selectedAccessory);
        setNotification({
          open: true,
          message: 'Accessory unmapped successfully',
          severity: 'success'
        });
      }
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || `Failed to ${actionType} accessory`,
        severity: 'error'
      });
    } finally {
      setDialogOpen(false);
      setSelectedAccessory(null);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAccessory(null);
    setActionType(null);
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  if (accessories.length === 0) {
    return (
      <Box sx={{
        textAlign: 'center',
        p: 4,
        bgcolor: '#f8f9fa',
        borderRadius: 2,
        border: '1px dashed #dee2e6',
        width: '100%'
      }}>
        <DevicesIcon sx={{ fontSize: 48, color: '#adb5bd', mb: 2 }} />
        <Typography variant="body1" sx={{ color: '#6c757d' }}>
          {isCardLevel
            ? 'No accessories found for this card.'
            : selectedCardId === 'all'
              ? 'No accessories found.'
              : 'No accessories found for selected card.'
          }
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {!isCardLevel && (
        <Box sx={{ mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
            <Select
              value={selectedCardId}
              onChange={(e) => setSelectedCardId(e.target.value)}
              displayEmpty
              sx={{
                fontSize: '0.875rem',
                borderRadius: '12px',
                '& .MuiSelect-select': {
                  borderRadius: '12px',
                  padding: '10px 14px',
                },
                '& fieldset': {
                  border: 'none',
                  borderRadius: '12px',
                }
              }}
            >
              <MenuItem value="all">
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Avatar
                    src={avatarPlaceholder}
                    sx={{
                      width: 24,
                      height: 24,
                      mr: 2
                    }}
                  />
                  <Typography sx={{ fontSize: '0.875rem' }}>
                    All Accessories
                  </Typography>
                </Box>
              </MenuItem>
              {userCards.map((card) => (
                <MenuItem key={card._id} value={card._id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Avatar
                      src={card.profilePictureUrl || avatarPlaceholder}
                      sx={{
                        width: 24,
                        height: 24,
                        mr: 2
                      }}
                    />
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      {card.contact_info?.fullName || 'Unnamed Card'}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Paper sx={{ width: '100%', mb: 2, overflow: 'auto' }}>
        <TableContainer sx={{ minWidth: { xs: 300, sm: 750 } }}>
          <Table aria-label="accessories table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: { xs: 80, sm: 120 } }}>Name</TableCell>
                <TableCell sx={{ minWidth: { xs: 100, sm: 150 } }}>Serial Number</TableCell>
                <TableCell sx={{ minWidth: { xs: 80, sm: 100 } }}>Status</TableCell>
                {!isCardLevel && <TableCell sx={{ minWidth: { xs: 100, sm: 150 } }}>Connected Card</TableCell>}
                <TableCell sx={{ minWidth: { xs: 100, sm: 120 } }}>Last Updated</TableCell>
                <TableCell sx={{ minWidth: { xs: 80, sm: 100 } }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accessories
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((accessory) => (
                  <TableRow
                    key={accessory.serialNumber}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {accessory.name || 'Accessory'}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        color: '#666'
                      }}>
                        {accessory.serialNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={accessory.status}
                        color={getStatusColor(accessory.status)}
                        size="small"
                        sx={{
                          fontSize: '0.75rem',
                          height: '24px',
                          fontWeight: 500,
                          textTransform: 'capitalize'
                        }}
                      />
                    </TableCell>
                    {!isCardLevel && (
                      <TableCell>
                        {accessory.content?.data?.cardId ? (
                          (() => {
                            const connectedCard = userCards.find(card => card._id === accessory.content.data.cardId);
                            return connectedCard ? (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                  src={connectedCard.profilePictureUrl || avatarPlaceholder}
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    mr: 2
                                  }}
                                />
                                <Typography sx={{ fontSize: '0.875rem' }}>
                                  {connectedCard.contact_info?.fullName || 'Unnamed Card'}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography sx={{ fontSize: '0.875rem', color: '#666' }}>
                                Unknown Card
                              </Typography>
                            );
                          })()
                        ) : (
                          <Typography sx={{ fontSize: '0.875rem', color: '#666' }}>
                            Not Connected
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {formatDate(accessory.updatedAt)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => onEditAccessory(accessory)}
                          sx={{
                            color: '#666',
                            '&:hover': {
                              backgroundColor: '#f5f5f5'
                            }
                          }}
                        >
                          <EditOutlinedIcon />
                        </IconButton>
                        {accessory.status === 'activated' && (
                          <IconButton
                            size="small"
                            onClick={() => handleActionClick(accessory, 'unmap')}
                            sx={{
                              color: '#666',
                              '&:hover': {
                                backgroundColor: '#e3f2fd',
                                color: 'primary.main'
                              }
                            }}
                          >
                            <LinkOffIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          component="a"
                          href={accessory.content?.data?.cardId ? `/card/${accessory.content.data.cardId}` : '#'}
                          target="_blank"
                          disabled={!accessory.content?.data?.cardId}
                          sx={{
                            color: '#000',
                            '&:hover': {
                              backgroundColor: '#f5f5f5'
                            }
                          }}
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={accessories.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="action-dialog-title"
      >
        <DialogTitle id="action-dialog-title">
          Unmap Accessory
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to unmap this accessory from the card? The accessory will be available for new activations.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              color: '#666',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleActionConfirm}
            variant="contained"
            color="primary"
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
            }}
          >
            Unmap
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        handleClose={handleCloseNotification}
      />
    </Box>
  );
};

export default AccessoryTable;